import { useState, useEffect } from "react";
import { Trash2, Pencil, Plus } from "lucide-react";
import { Button, Modal, Select, Table } from "../../../components";
import TimePicker from "../../../components/TimePicker/TimePicker";
import DatePicker from "../../../components/DatePicker/DatePicker";
import axios from "axios";
import { TRAININGS_URL, getServerUrl } from "../../../features/UrlHelper";
import { handleApiError } from "../../../features/errorHandler";

const DAYS_SK = [
  "Nedeľa",
  "Pondelok",
  "Utorok",
  "Streda",
  "Štvrtok",
  "Piatok",
  "Sobota",
];

const EMPTY_FORM = {
  id: null,
  title: "",
  description: "",
  event_day: "",
  time_from: "",
  time_to: "",
  color: "#F97316",
  valid_from: "",
  valid_to: "",
};


const authHeader = () => ({
  Authorization: `Bearer ${window.localStorage.getItem("authToken")}`,
});

function TrainingsSection() {
  const [trainings, setTrainings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingTraining, setEditingTraining] = useState(null);
  const [trainingForm, setTrainingForm] = useState(EMPTY_FORM);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    loadEvents();
  }, []);

  async function loadEvents() {
    try {
      const response = await axios.get(TRAININGS_URL, {
        headers: authHeader(),
      });
      setTrainings(response.data);
    } catch (error) {
      handleApiError(error, "Nepodarilo sa načítať tréningy.");
    } finally {
      setLoading(false);
    }
  }

  const openAdd = () => {
    setEditingTraining(null);
    setTrainingForm(EMPTY_FORM);
    setModalOpen(true);
  };

  const openEdit = (training) => {
    setEditingTraining(training);
    setTrainingForm({
      id: training.id,
      title: training.title,
      description: training.description ?? "",
      event_day: String(training.event_day),
      time_from: training.time_from,
      time_to: training.time_to,
      color: training.color ?? "#F97316",
      valid_from: training.valid_from ?? "",
      valid_to: training.valid_to ?? "",
    });
    setModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...trainingForm,
      event_day: Number(trainingForm.event_day),
      valid_from: trainingForm.valid_from || null,
      valid_to: trainingForm.valid_to || null,
    };
    try {
      if (editingTraining) {
        await axios.post(
          TRAININGS_URL,
          { id: editingTraining.id, ...payload },
          { headers: authHeader() },
        );
      } else {
        await axios.put(TRAININGS_URL, payload, { headers: authHeader() });
      }
      setModalOpen(false);
      loadEvents();
    } catch (error) {
      handleApiError(error, { showAlert: true, fallbackMessage: "Nepodarilo sa uložiť tréning." });
    }
  };

  const deleteTraining = async (id) => {
    try {
      await axios.delete(`${TRAININGS_URL}/${id}`, {
        headers: authHeader(),
      });
      setDeleteConfirm(null);
      loadEvents();
    } catch (error) {
      handleApiError(error, { showAlert: true, fallbackMessage: "Nepodarilo sa zmazať tréning." });
    }
  };

  return (
    <div className="admin__section">
      <div className="admin__sub-header">
        <h2 className="admin__section-title">Plánovanie tréningov</h2>
        <Button variant="primary" size="sm" onClick={openAdd}>
          <Plus size={16} /> Pridať tréning
        </Button>
      </div>

      <Table
        columns={[
          {
            key: "title",
            label: "Názov",
            width: "18%",
            render: (val) => <strong className="admin__table-ellipsis">{val}</strong>,
          },
          {
            key: "description",
            label: "Popis",
            width: "22%",
            render: (val) => (
              <span className="admin__table-ellipsis" title={val ?? ""}>
                {val ?? "—"}
              </span>
            ),
          },
          {
            key: "event_day",
            label: "Deň",
            width: "10%",
            render: (val) => DAYS_SK[val] ?? val,
          },
          {
            key: "time_from",
            label: "Čas",
            width: "12%",
            sortable: false,
            render: (_, row) => (
              <span className="admin__table-ellipsis">
                {row.time_from?.slice(0, 5) ?? "—"}–{row.time_to?.slice(0, 5) ?? "—"}
              </span>
            ),
          },
          {
            key: "valid_from",
            label: "Platnosť",
            width: "18%",
            sortable: false,
            render: (_, row) => {
              const from = row.valid_from ? new Date(row.valid_from).toLocaleDateString("sk-SK") : null;
              const to = row.valid_to ? new Date(row.valid_to).toLocaleDateString("sk-SK") : null;
              return (
                <span className="admin__table-ellipsis">
                  {!from && !to ? "—" : `${from ?? "…"} – ${to ?? "…"}`}
                </span>
              );
            },
          },
          {
            key: "color",
            label: "Farba",
            width: "8%",
            sortable: false,
            render: (val) => (
              <span
                style={{
                  display: "inline-block",
                  width: 18,
                  height: 18,
                  borderRadius: 4,
                  backgroundColor: val ?? "transparent",
                  border: "1px solid #ccc",
                }}
              />
            ),
          },
          {
            key: "actions",
            label: "Akcie",
            width: "12%",
            sortable: false,
            render: (_, row) => (
              <div className="admin__table-actions">
                <button className="admin__icon-btn" onClick={() => openEdit(row)} title="Upraviť">
                  <Pencil size={16} />
                </button>
                <button className="admin__icon-btn admin__icon-btn--danger" onClick={() => setDeleteConfirm(row.id)} title="Zmazať">
                  <Trash2 size={16} />
                </button>
              </div>
            ),
          },
        ]}
        data={trainings}
        searchPlaceholder="Hľadať tréning..."
        emptyMessage={loading ? "Načítava sa…" : "Žiadne tréningy"}
      />

      {/* Add / Edit training modal */}
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editingTraining ? "Upraviť tréning" : "Pridať tréning"}
      >
        <form className="admin__form" onSubmit={handleSubmit}>
          <div className="admin__form-field">
            <label className="admin__form-label">Názov</label>
            <input
              maxLength={100}
              className="admin__form-input"
              value={trainingForm.title}
              onChange={(e) =>
                setTrainingForm((prev) => ({ ...prev, title: e.target.value }))
              }
              placeholder="Napr. Začiatočníci"
              required
            />
          </div>
          <div className="admin__form-field">
            <label className="admin__form-label">Popis</label>
            <input
              maxLength={255}
              className="admin__form-input"
              value={trainingForm.description}
              onChange={(e) =>
                setTrainingForm((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
              placeholder="Nepovinný popis"
            />
          </div>
          <div className="admin__form-field">
            <Select
              label="Deň v týždni"
              placeholder="Vyberte deň"
              value={trainingForm.event_day}
              onChange={(val) =>
                setTrainingForm((prev) => ({ ...prev, event_day: val }))
              }
              options={DAYS_SK.map((name, idx) => ({
                value: String(idx),
                label: name,
              }))}
            />
          </div>
          <div className="admin__form-row">
            <div className="admin__form-field">
              <TimePicker
                label="Čas od"
                value={trainingForm.time_from}
                onChange={(val) =>
                  setTrainingForm((prev) => ({
                    ...prev,
                    time_from: val,
                  }))
                }
                required
              />
            </div>
            <div className="admin__form-field">
              <TimePicker
                label="Čas do"
                value={trainingForm.time_to}
                onChange={(val) =>
                  setTrainingForm((prev) => ({
                    ...prev,
                    time_to: val,
                  }))
                }
                required
              />
            </div>
          </div>
          <div className="admin__form-row">
            <div className="admin__form-field">
              <DatePicker
                label="Platnosť od"
                value={trainingForm.valid_from}
                onChange={(val) =>
                  setTrainingForm((prev) => ({
                    ...prev,
                    valid_from: val,
                  }))
                }
              />
            </div>
            <div className="admin__form-field">
              <DatePicker
                label="Platnosť do"
                value={trainingForm.valid_to}
                onChange={(val) =>
                  setTrainingForm((prev) => ({
                    ...prev,
                    valid_to: val,
                  }))
                }
              />
            </div>
          </div>
          <div className="admin__form-field">
            <label className="admin__form-label">Farba</label>
            <input
              type="color"
              className="admin__form-input"
              value={trainingForm.color}
              onChange={(e) =>
                setTrainingForm((prev) => ({ ...prev, color: e.target.value }))
              }
            />
          </div>
          <div className="admin__form-actions">
            <Button
              variant="ghost"
              type="button"
              onClick={() => setModalOpen(false)}
            >
              Zrušiť
            </Button>
            <Button variant="primary" type="submit">
              {editingTraining ? "Uložiť zmeny" : "Pridať tréning"}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete confirmation */}
      <Modal
        isOpen={deleteConfirm !== null}
        onClose={() => setDeleteConfirm(null)}
        title="Potvrdiť zmazanie"
      >
        <p>Naozaj chcete zmazať tento tréning? Táto akcia sa nedá vrátiť.</p>
        <div className="admin__form-actions">
          <Button variant="ghost" onClick={() => setDeleteConfirm(null)}>
            Zrušiť
          </Button>
          <Button
            variant="primary"
            onClick={() => deleteTraining(deleteConfirm)}
            style={{ backgroundColor: "#EF4444", borderColor: "#EF4444" }}
          >
            Zmazať
          </Button>
        </div>
      </Modal>
    </div>
  );
}

export default TrainingsSection;
